import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ExternalLink, PenLine, BookOpen, MessageCircleQuestion,
  FlaskConical, Wrench, Wheat, ScrollText
} from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  ["RECIPES", "Рецепты", "Законченные бесплатные рецепты, опубликованные в социальных сетях и специально подготовленные для Anokina Studio.", BookOpen],
  ["QUESTIONS", "Ответы на ваши вопросы", "Понятные ответы на реальные повторяющиеся вопросы аудитории.", MessageCircleQuestion],
  ["TECHNOLOGY", "Технологии", "Почему это работает: процессы, признаки готовности, хранение и профессиональные объяснения.", FlaskConical],
  ["TROUBLESHOOTING", "Ошибки и решения", "Быстрая практическая помощь, когда что-то пошло не так.", Wrench],
  ["INGREDIENTS", "Ингредиенты", "Самостоятельные материалы об ингредиентах и их роли в рецептуре.", Wheat],
  ["HISTORY", "Истории и традиции", "История десертов, национальные рецепты и гастрономические традиции.", ScrollText],
];

export default function OpenLibraryWorkspace() {
  const nav = useNavigate();
  const [selectedSection, setSelectedSection] = useState("RECIPES");
  const activeSection = SECTIONS.find(([id]) => id === selectedSection) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-[#F4EBDD]" data-testid="open-library-workspace">
      <header className="sticky top-0 z-30 border-b border-[#DCCDB8]/80 bg-[#F8F1E6]/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-3">
          <button onClick={() => nav("/admin?tab=products")} className="p-2 -ml-2 rounded-full hover:bg-white/60" aria-label="К продуктам">
            <ChevronLeft className="w-5 h-5 text-espresso" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.32em] text-[#8E745A]">Anokina Studio · управление</div>
            <h1 className="font-serif text-xl md:text-2xl text-espresso truncate -mt-0.5">Открытая библиотека</h1>
          </div>
          <Button onClick={() => nav("/admin/editorial")} variant="outline" className="hidden md:inline-flex rounded-full bg-white/50">
            <PenLine className="mr-2 h-4 w-4" /> Редакция
          </Button>
          <Button onClick={() => window.open("/library", "_blank")} variant="outline" className="hidden md:inline-flex rounded-full bg-white/50">
            <ExternalLink className="mr-2 h-4 w-4" /> Как читатель
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-7">
        <section className="rounded-[28px] border border-[#D7C5AD] bg-[#EADBC5] p-7 md:p-10">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.34em] text-[#8A6947]">Открытое пространство знаний</div>
            <h2 className="mt-4 font-serif text-5xl leading-[0.95] text-[#332015]">Открытая библиотека</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-[#68523F]">
              Живая коллекция рецептов, историй и ответов. Материалы создаются один раз в Редакции и публикуются сюда без копирования.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              <Button onClick={() => nav("/admin/editorial")} className="rounded-full bg-[#4A3425] px-6 text-[#FFF7EA] hover:bg-[#3A281D]">
                <PenLine className="mr-2 h-4 w-4" /> Открыть Редакцию
              </Button>
              <Button onClick={() => window.open("/library", "_blank")} variant="outline" className="rounded-full border-[#9B7B59]/40 bg-white/35 px-6">
                <ExternalLink className="mr-2 h-4 w-4" /> Открыть как читатель
              </Button>
            </div>
          </div>
        </section>

        <section className="mt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map(([id, title, description, Icon]) => {
              const active = selectedSection === id;
              return (
              <button key={id} type="button"
                onClick={() => setSelectedSection(id)}
                className={active
                  ? "min-h-[230px] rounded-[24px] border border-[#B99C79] bg-[#EADCC8] p-6 text-left transition flex flex-col"
                  : "min-h-[230px] rounded-[24px] border border-[#D7C5AD] bg-white/50 p-6 text-left transition hover:bg-white/70 flex flex-col"}>
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#AA8A68]/35 bg-[#EFE3D2] text-[#6B4E35]">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-5 font-serif text-2xl text-[#3A281E]">{title}</div>
                <p className="mt-2 text-sm leading-6 text-[#765F49] line-clamp-3">{description}</p>
              </button>
            )})}
          </div>
        </section>

        <section className="mt-6 rounded-[24px] border border-[#D7C5AD] bg-[#FAF5EC]/75 p-6 md:p-8" data-testid="open-library-owner-section">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6947]">Выбранный раздел</div>
          <h3 className="mt-2 font-serif text-3xl text-[#332015]">{activeSection[1]}</h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[#705945]">{activeSection[2]}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={() => nav("/admin/editorial")} className="rounded-full bg-[#4A3425] px-5 text-[#FFF7EA] hover:bg-[#3A281D]">
              <PenLine className="mr-2 h-4 w-4" /> Создать или редактировать в Редакции
            </Button>
            <Button onClick={() => window.open(`/library?section=${activeSection[0]}`, "_blank")} variant="outline" className="rounded-full border-[#9B7B59]/40 bg-white/55 px-5">
              <ExternalLink className="mr-2 h-4 w-4" /> Посмотреть этот раздел как читатель
            </Button>
          </div>
          <div className="mt-5 rounded-2xl border border-dashed border-[#D6C4AA] bg-white/40 p-5 text-sm text-[#78624D]">
            Опубликованные материалы этого раздела появятся здесь после первого наполнения Открытой библиотеки через Редакцию.
          </div>
        </section>

        <section className="mt-6 rounded-[24px] border border-[#D7C5AD] bg-white/50 p-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6947]">Как управлять</div>
          <div className="mt-2 font-serif text-2xl text-[#332015]">Публикация идёт через Редакцию</div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-[#705945]">
            В материале выбери «Где будет опубликовано → Открытая библиотека». Здесь не создаётся вторая копия материала. После публикации он появляется у читателей в Открытой библиотеке.
          </p>
        </section>
      </main>
    </div>
  );
}
